import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import { CircularProgress } from "@mui/material";
import { ReactComponent as CopyIcon } from "../../../../assets/Copy.svg";
import SearchIcon from "../../../../assets/searchIconGreen.svg";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { RpaServices } from "../../../../services/RpaServices";
import { HospitalServices } from "../../../../services/HospitalServices";
import { ReportServices } from "../../../../services/ReportServices";
import { InterUserMangementServices } from "../../../../services/InternalUserMangementServices";
import { handleApiErrorResponses } from "../../../../utils/ApisErrorHandling";
import TmsQueries from "../../TmsQueries";
import { getUser } from "../../../../config/user";
import { toast } from "react-toastify";
import CountdownTimer from "../../../../utils/countDownTimer";
import CustomPagination from "../../../../utils/paginationUtils";

function DutPage() {
  const [queryData, setQueryData] = useState([]);
  const [totalPages, setTotalPages] = useState(10);
  const [queryCount, setQueryCount] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [searchField, setSearchField] = useState("patientname");
  const [searchValue, setSearchValue] = useState("");
  const [selectedQueryType, setSelectedQueryType] = useState("");
  const [hospitalId, setHospitalId] = useState("");
  const [hospitalList, setHospitalList] = useState([]);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [caseId, setCaseId] = useState(null);
  const [reassignOpenId, setReassignOpenId] = useState(null);
  const [reassignOpenUpById, setReassignOpenUpById] = useState({});
  const [reassignUsersByQueryId, setReassignUsersByQueryId] = useState({});
  const [reassignLoadingId, setReassignLoadingId] = useState(null);
  const [unblurQueryIds, setUnblurQueryIds] = useState({});
  const [, setTick] = useState(0);
  const [caseQueryCounts, setCaseQueryCounts] = useState({
    admissionQueryCount: 0,
    enhancementQueryCount: 0,
    claimQueryCount: 0,
  });
  const [usersMap, setUsersMap] = useState(null);
  const searchRef = useRef();
  const user = getUser();
  const myUserId = user?.user?.id ?? user?.id;

  const getWipTimer = (wipAt) => {
    if (!wipAt) return "00:00:00";
    const seconds = Math.max(
      0,
      Math.floor((Date.now() - new Date(wipAt)) / 1000),
    );
    const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const getNameByUserId = (users, userId) => {
    if (!userId) return "";
    if (users && !Array.isArray(users)) {
      return users[String(userId)] || "";
    }
    const found = (users || []).find(
      (item) => String(item?.userId ?? item?.id) === String(userId),
    );
    return found?.fullName || found?.user?.fullName || "";
  };

  const getTeamUserDisplayName = (item) => {
    const id = item?.userId || item?.id;
    return getNameByUserId(usersMap, id) || item?.fullName || "Name";
  };

  const getCoordinatorUsers = async () => {
    const res = await InterUserMangementServices.getDutTeamUsers({
      limit: 100,
      page: 1,
    });
    const users = res?.data?.teamUsers || [];

    return users.map((item) => ({
      ...item,
      userId: item.userId || item.id,
      user: item.user || item,
    }));
  };

  const getReassignTeamUsers = async () => {
    const ccDoctorRes = await InterUserMangementServices.getCcDoctorTeamUsers({
      limit: 100,
      page: 1,
    });

    const allUsers = [...(ccDoctorRes?.data?.teamUsers || [])];

    const uniqueMap = new Map();
    allUsers.forEach((item) => {
      const id = item?.userId || item?.id || item?.user?.id;
      if (!id || uniqueMap.has(String(id))) return;
      uniqueMap.set(String(id), {
        ...item,
        userId: item.userId || item.id || item?.user?.id,
        user: item.user || item,
      });
    });

    return Array.from(uniqueMap.values());
  };

  const isInternalAdmin = (item) => {
    const fullName = item?.user?.fullName ?? item?.fullName ?? "";
    const roleName =
      item?.user?.roleName ??
      item?.roleName ??
      item?.roles?.[0]?.role?.roleName ??
      "";

    return (
      fullName.toLowerCase().replace(/\s/g, "") === "internaladmin" ||
      roleName.toLowerCase().replace(/\s/g, "") === "internaladmin"
    );
  };

  const copyToClipboard = async (text, queryId) => {
    try {
      await navigator.clipboard.writeText(text);
      // setCopiedMessageId(queryId);
      // setTimeout(() => setCopiedMessageId(null), 2000);
      toast.success("Query copied successfully");
    } catch (err) {
      alert("Failed to copy to clipboard");
    }
  };

  const fetchCaseQueryCounts = async () => {
    const userId = user.roles.some((role) => role.roleId === 7)
      ? ""
      : user?.user?.id;
    try {
      const response = await RpaServices.getCaseQueryCounts(hospitalId, userId);
      setCaseQueryCounts(response.data);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };
  const fetchQueries = async () => {
    setIsLoading(true);
    try {
      //Checking if the user is Admin or not
      const userId = user.roles.some((role) => role.roleId === 7)
        ? ""
        : user?.user?.id;
      const res = await RpaServices.manageQueries(
        "ACTIVE",
        page,
        10,
        searchValue,
        hospitalId,
        selectedQueryType,
        "",
        userId,
        true,
      );
      const rows = Array.isArray(res?.data?.queries) ? res.data.queries : [];
      const formattedData = rows.map((q) => {
        const assignedTo = q?.case?.currentSubMasterStage?.assignedTo;
        const assignId = assignedTo || q?.wipTo;
        return {
          case: q?.case,
          id: q?.id,
          patientName: q?.case?.patient?.patientName,
          hospitalName: q?.case?.hospital?.name,
          hospitalId: q?.case?.hospital?.code,
          pmjayId: q?.case?.pmjayId,
          pmjayRegistrationId: q?.case?.pmjayRegistrationId,
          queryMessage: q?.queryMessage,
          dutMessage: JSON.parse(q?.forwardComment || "{}")?.comment || "-",
          registeredDate: q?.case?.patient?.registeredDate,
          tmsStageUpdateTime: q?.tmsStageUpdateTime,
          resolveTatTime: q?.deadline,
          caseId: q?.case?.id,
          commandCenterStage:
            q?.case?.currentSubMasterStage?.commandCenterStage,
          commandCenterStageId: q?.case?.currentSubMasterStage?.id,
          wipTo: assignId,
          wipAt: q?.wipAt,
          assignedTo: q?.case?.currentSubMasterStage?.assignedTo || null,
          doctorName:
            q?.wipToName ||
            q?.wipToUser?.fullName ||
            getNameByUserId(usersMap, assignId),
          wip: q?.wip || q?.isWip || false,
        };
      });

      let coordinatorUsers = null;
      const usersByQueryId = {};

      for (const item of formattedData) {
        if (!item.wipTo || item.doctorName) {
          continue;
        }

        const nameFromUsers = getNameByUserId(usersMap, item.wipTo);
        if (nameFromUsers) {
          item.doctorName = nameFromUsers;
          continue;
        }

        if (!coordinatorUsers) {
          try {
            coordinatorUsers = await getReassignTeamUsers();
          } catch {
            coordinatorUsers = [];
          }
        }

        usersByQueryId[item.id] = coordinatorUsers;
        item.doctorName = getNameByUserId(usersByQueryId[item.id], item.wipTo);
      }

      setReassignUsersByQueryId((prev) => ({ ...prev, ...usersByQueryId }));
      setQueryData(formattedData);
      setQueryCount(res?.data?.total);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (e) {
      handleApiErrorResponses(e);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    (async () => {
      try {
        const res = await HospitalServices.gethospitals("", 1, 1500);
        setHospitalList(res?.data?.list || []);
      } catch (e) {
        handleApiErrorResponses(e);
      }
    })();
    fetchCaseQueryCounts();
    ReportServices.getUsers()
      .then((res) => setUsersMap(res?.data?.users ?? null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchQueries();
  }, [page, hospitalId, searchField, searchValue, selectedQueryType, usersMap]);

  useEffect(() => {
    const timer = setInterval(() => setTick((count) => count + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const closeDropdown = () => setReassignOpenId(null);
    document.addEventListener("click", closeDropdown);
    return () => document.removeEventListener("click", closeDropdown);
  }, []);

  const handleReassignClick = async (q, e) => {
    e?.stopPropagation?.();
    if (reassignOpenId === q.id) {
      setReassignOpenId(null);
      return;
    }

    const rect = e?.currentTarget?.getBoundingClientRect?.();
    const spaceBelow = rect ? window.innerHeight - rect.bottom : 0;
    const spaceAbove = rect ? rect.top : 0;
    const estimatedDropdownHeight = 210;
    const shouldOpenUp =
      spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;
    setReassignOpenUpById((prev) => ({ ...prev, [q.id]: shouldOpenUp }));
    setReassignOpenId(q.id);

    if ((reassignUsersByQueryId[q.id] || []).length > 0) return;

    try {
      setReassignLoadingId(q.id);
      const users = await getReassignTeamUsers();
      setReassignUsersByQueryId((prev) => ({
        ...prev,
        [q.id]: users,
      }));
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setReassignLoadingId(null);
    }
  };

  const handleReassignUserPick = async (q, selectedUser) => {
    const wipTo =
      selectedUser?.userId || selectedUser?.id || selectedUser?.user?.id;
    // if (!q?.id || !wipTo) return;

    try {
      // const response = await RpaServices.wipAssignCaseQuery(q.id, wipTo, true);
      const response = await assignCaseStageForQuery(q, wipTo, true);
      if (response?.data?.message) toast.success(response.data.message);
      await fetchQueries();
      // setQueryData((list) =>
      //   list.map((item) =>
      //     item.id === q.id
      //       ? {
      //           ...item,
      //           wip: true,
      //           wipTo,
      //           wipAt: new Date().toISOString(),
      //           doctorName: getTeamUserDisplayName(selectedUser),
      //         }
      //       : item,
      //   ),
      // );
      setReassignOpenId(null);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  const assignCaseStageForQuery = async (q, userId, reAssign = false) => {
    if (!q?.commandCenterStageId || !userId) return;

    await ReportServices.assignCaseStage(
      q.commandCenterStageId,
      userId,
      reAssign,
    );
  };

  const handleProceedClick = async (q) => {
    if (!q?.caseId) return;

    // // const isOwner = String(q.wipTo) === String(myUserId);
    // const isFree = !q.wip;

    // if (!isFree && !isOwner) return;

    try {
      if (String(q?.assignedTo) !== String(myUserId)) {
        // const response = await RpaServices.wipAssignCaseQuery(
        //   q.id,
        //   myUserId,
        //   false,
        // );
        const response = await assignCaseStageForQuery(q, myUserId, false);

        if (response?.data?.message) toast.success(response.data.message);
      }

      setSelectedQuery(q);
      setCaseId(q.caseId);
    } catch (error) {
      handleApiErrorResponses(error);
      await fetchQueries();

      // if (isOwner) {
      //   setSelectedQuery(q);
      //   setCaseId(q.caseId);
      // }
    }
  };

  const handleCloseQueryModal = () => {
    setSelectedQuery(null);
    fetchQueries();
  };

  const fetchQueriesAfterForward = async () => {
    await new Promise((resolve) => setTimeout(resolve, 300));
    await fetchQueries();
  };
  //testing dut page
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";

    const date = new Date(dateStr);

    const time = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const day = date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    return `${time}\n${day}`;
  };

  const handleSearch = (e) => {
    const value = e.target.value;

    clearTimeout(searchRef.current);

    searchRef.current = setTimeout(() => {
      // remove leading/trailing spaces
      const trimmedValue = value.trim();

      // allow empty value OR more than 2 characters
      if (trimmedValue === "" || trimmedValue.length > 2) {
        setSearchValue(trimmedValue);
      }
    }, 400);
  };

  const toggleMessage = (id) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderMessage = (text, id) => {
    if (!text) return "-";

    const words = text.split(/\s+/);

    // first 6 words preview
    const previewWords = words.slice(0, 6).join(" ");

    const hasMore = words.length > 6;

    const isExpanded = expandedMessages[id];

    console.log("query data:", queryData);
    return (
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0 pr-2">
          {isExpanded ? (
            <>
              {text
                .split("\n")
                .filter(Boolean)
                .map((msg, idx) => (
                  <div
                    key={idx}
                    className="mb-1 text-[14px] font-[Lufga] leading-[150%]"
                  >
                    {idx + 1}) {msg}
                  </div>
                ))}

              <span
                onClick={() => toggleMessage(id)}
                className="text-[#017C57] cursor-pointer text-[14px] font-medium hover:opacity-70 mt-1 inline-block"
              >
                Show Less
              </span>
            </>
          ) : (
            <div className="flex items-start flex-wrap">
              <span className="text-[14px] font-[Lufga] leading-[150%] break-words">
                {previewWords}
                {hasMore && "..."}
              </span>

              {hasMore && (
                <span
                  onClick={() => toggleMessage(id)}
                  className="ml-1 text-[#017C57] cursor-pointer text-[14px] font-medium hover:opacity-70 whitespace-nowrap"
                >
                  showmore
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="w-full min-w-0 p-6 pt-24 font-[Lufga]"
      style={{ scrollbarGutter: "stable" }}
    >
      <div className="mb-4 flex min-w-0 items-start">
        <h2 className="min-w-0 flex-1 text-2xl font-semibold">
          Manage Query ({queryCount})
        </h2>

        {/* <div className="flex items-center gap-2 px-4 py-4 rounded-full border border-solid border-green-700 bg-white w-[430px] flex-shrink-0 mt-10">
          <input
            type="text"
            onChange={handleSearch}
            placeholder="Search by registration ID, patient name, hospital name"
            className="outline-none border-none w-full min-w-0 bg-transparent text-green-700 placeholder:text-green-700"
          />
        </div> */}
        <div className="flex items-center gap-2 px-4 py-4 rounded-full border border-solid border-green-700 bg-white w-[390px] flex-shrink-0">
          {" "}
          <img src={SearchIcon} alt="search-icon" className="w-4 h-4" />
          <input
            type="text"
            className="outline-none border-none w-full min-w-0 bg-transparent text-green-700 placeholder:text-green-700"
            onChange={handleSearch}
            placeholder={`Search by registration ID, patient name, hospital name`}
          />
        </div>
      </div>

      <div className="mt-6 mb-4 flex min-w-0 flex-nowrap items-center gap-4 overflow-x-auto pb-1">
        <button
          type="button"
          onClick={() => {
            setSelectedQueryType("");
            setPage(1);
          }}
          className={`h-14 w-[180px] shrink-0 flex items-center justify-center text-xs 2xl:text-sm rounded-full font-[Lufga] border-2 transition-all whitespace-nowrap border-solid
        ${
          selectedQueryType === ""
            ? "bg-[#017C57] text-white border-[#017C57]"
            : "bg-white text-[#017C57] border-[#017C57] hover:bg-[#017C57] hover:text-white"
        }
      `}
        >
          All (
          {caseQueryCounts?.admissionQueryCount +
            caseQueryCounts?.enhancementQueryCount +
            caseQueryCounts?.claimQueryCount}
          )
        </button>
        {[
          {
            key: "admission",
            label: `Admission (${caseQueryCounts?.admissionQueryCount || 0})`,
          },
          {
            key: "enhancement",
            label: `Enhancement (${caseQueryCounts?.enhancementQueryCount || 0})`,
          },
          {
            key: "claim",
            label: `Claim (${caseQueryCounts?.claimQueryCount || 0})`,
          },
        ].map((btn) => (
          <button
            key={btn.key}
            type="button"
            onClick={() => {
              setSelectedQueryType(btn.key);
              setPage(1);
            }}
            className={`h-14 w-[180px] shrink-0 flex items-center justify-center text-xs 2xl:text-sm rounded-full font-[Lufga] border-2 transition-all whitespace-nowrap border-solid
        ${
          selectedQueryType === btn.key
            ? "bg-[#017C57] text-white border-[#017C57]"
            : "bg-white text-[#017C57] border-[#017C57] hover:bg-[#017C57] hover:text-white"
        }
      `}
          >
            {btn.label}
          </button>
        ))}

        {/* <div
          className="ml-auto px-4 py-2 rounded-full bg-white min-w-[260px] border-solid"
          style={{ border: "2px solid #017C57" }}
        >
          <Select
            name="hospitalId"
            isClearable
            options={hospitalList}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            onChange={(item) => setHospitalId(item ? item.id : "")}
            value={hospitalList.find((item) => item.id === hospitalId) || null}
            placeholder="FILTER HOSPITAL WISE"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              control: (base) => ({
                ...base,
                border: "none",
                boxShadow: "none",
                minHeight: "unset",
              }),
              singleValue: (base) => ({ ...base, color: "#017C57" }), // selected value color
              placeholder: (base) => ({ ...base, color: "#017C57" }), // placeholder color
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              indicatorSeparator: () => ({ display: "none" }),
            }}
          />
        </div> */}
      </div>
      <div className="border-t border-b-0  border-solid border-[#E8E8E8] my-6" />

      {isLoading ? (
        <div className="flex justify-center">
          <CircularProgress />
        </div>
      ) : (
        // <div className="bg-white rounded-[20px] p-4 shadow-[0_0_20px_rgba(0,0,0,0.08)]">
        <div className="w-full min-w-0 overflow-x-auto">
          <div className="min-w-[1200px] min-h-[50vh] rounded-[20px] bg-white p-4 shadow-[0_0_20px_rgba(0,0,0,0.08)]">
            {/* <div className="grid grid-cols-7 gap-5 font-semibold mb-3"> */}
            <div className="grid w-full grid-cols-10 items-start gap-5 font-semibold mb-3">
              <div>Patient</div>
              {/* <div>Hospital Name</div>
            <div>HospitalID</div> */}
              <div className="">Registration ID</div>
              <div className="col-span-3 text-center">Query</div>
              <div className="">DUT Message</div>
              <div className="">Query due</div>
              <div>Resolve TAT</div>
              <div>Assign to</div>
              <div className="text-center">Action</div>
            </div>
            {queryData?.length > 0 ? (
              queryData.map((q, i) => {
                const isUnblurred = unblurQueryIds[q.id];
                const shouldApplyBlur =
                  q?.assignedTo != null &&
                  String(q.assignedTo) !== String(myUserId);

                const hideData = shouldApplyBlur && !isUnblurred;
                const showReassignButton = isInternalAdmin(user);
                const shouldOpenUp = !!reassignOpenUpById[q.id];

                return (
                  <div
                    key={q.id}
                    className={`mb-2 grid w-full grid-cols-10 items-start gap-5  overflow-visible p-1 ${
                      i % 2 === 0 ? "bg-white" : "bg-gray-100"
                    } ${reassignOpenId === q.id ? "relative z-50" : "relative"}`}
                  >
                    <div className={hideData ? "blur-[10px]" : ""}>
                      {q.patientName || "-"}
                    </div>
                    {/* <div>{q.hospitalName || "-"}</div>

        
              <div>{q.hospitalId || "-"}</div> */}

                    {/* Registration ID → shifted slightly left */}
                    <div className={hideData ? "blur-[10px]" : ""}>
                      {q.pmjayRegistrationId || q.pmjayId || "-"}
                    </div>
                    {/* <div className="text-left -ml-24 ">
                {q.queryMessage || "-"}
                <CopyIcon />
              </div> */}
                    <div className="relative flex min-w-0 items-start justify-between gap-2 overflow-visible col-span-3">
                      <span
                        className={`min-w-0 flex-1 break-words pr-2 ${
                          hideData ? "blur-[10px]" : ""
                        }`}
                      >
                        {renderMessage(q.queryMessage || "", q.id)}
                      </span>
                      <button
                        type="button"
                        title="Copy query"
                        className={`mt-0.5 shrink-0 cursor-pointer border-none bg-transparent p-0 outline-none ${
                          hideData ? "blur-[10px]" : ""
                        }`}
                        onClick={() => copyToClipboard(q.queryMessage, q.id)}
                      >
                        <CopyIcon className="pointer-events-none h-5 w-5" />
                      </button>
                      {shouldApplyBlur && (
                        <div className="absolute inset-0 z-10 grid grid-cols-[18px_84px_76px_117px] xl:grid-cols-[18px_96px_82px_117px] 2xl:grid-cols-[18px_110px_88px_117px] items-center gap-2 whitespace-nowrap text-[#191919]">
                          <button
                            type="button"
                            className="bg-transparent border-none p-0 cursor-pointer flex items-center justify-center"
                            onClick={() => {
                              // setUnblurQueryIds((prev) => ({
                              //   ...prev,
                              //   [q.id]: !prev[q.id],
                              // }));
                            }}
                          >
                            <VisibilityOffOutlinedIcon className="!w-4 !h-4 shrink-0" />
                          </button>
                          <span className="whitespace-normal break-normal text-[12px] font-semibold leading-[14px]">
                            {getNameByUserId(usersMap, q.assignedTo)}
                          </span>
                          <span className="flex items-center gap-1 text-[12px] font-semibold tabular-nums">
                            <AccessTimeOutlinedIcon className="!w-4 !h-4 shrink-0" />
                            {getWipTimer(q.wipAt)}
                          </span>
                          {!q.reassignDone && showReassignButton && (
                            <div
                              className="relative shrink-0 w-[117px]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className={`h-10 w-full border border-[#017C57] bg-transparent px-4 text-[12px] font-medium text-[#191919] border-solid flex items-center justify-center gap-2 ${
                                  reassignOpenId === q.id
                                    ? shouldOpenUp
                                      ? "rounded-b-[24px] rounded-t-none border-t-0"
                                      : "rounded-t-[24px] rounded-b-none border-b-0"
                                    : "rounded-full"
                                }`}
                                onClick={(e) => handleReassignClick(q, e)}
                              >
                                <span>RE - ASSIGN</span>
                                {reassignOpenId === q.id ? (
                                  <KeyboardArrowUpIcon className="!w-5 !h-5 text-[#191919]" />
                                ) : (
                                  <KeyboardArrowDownIcon className="!w-5 !h-5 text-[#191919]" />
                                )}
                              </button>

                              {reassignOpenId === q.id && (
                                <div
                                  className={`absolute left-0 w-full bg-white z-50 ${
                                    shouldOpenUp ? "bottom-full" : "top-full"
                                  }`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {reassignLoadingId === q.id ? (
                                    <div className="p-3 text-center">
                                      <CircularProgress size={18} />
                                    </div>
                                  ) : (
                                    <Select
                                      autoFocus
                                      menuIsOpen
                                      isSearchable
                                      tabSelectsValue
                                      controlShouldRenderValue={false}
                                      menuPlacement={
                                        shouldOpenUp ? "top" : "bottom"
                                      }
                                      placeholder="Search"
                                      onKeyDown={(e) => {
                                        const currentInput =
                                          e?.target?.value || "";
                                        if (
                                          e.key === " " &&
                                          String(currentInput).trim().length ===
                                            0
                                        ) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }
                                      }}
                                      components={{
                                        DropdownIndicator: () => null,
                                      }}
                                      options={(
                                        reassignUsersByQueryId[q.id] || []
                                      ).map((item) => ({
                                        label: getTeamUserDisplayName(item),
                                        value: String(
                                          item.userId ||
                                            item?.user?.id ||
                                            item.id,
                                        ),
                                        user: item,
                                      }))}
                                      onChange={(option) =>
                                        option?.user &&
                                        handleReassignUserPick(q, option.user)
                                      }
                                      styles={{
                                        control: (base, state) => ({
                                          ...base,
                                          borderColor: "#017C57",
                                          borderLeft: "1px solid #017C57",
                                          borderRight: "1px solid #017C57",
                                          borderTop: "1px solid #017C57",
                                          borderBottom: "1px solid #017C57",
                                          borderRadius: 0,
                                          boxShadow: "none",
                                          outline: "none",
                                          "&:hover": {
                                            borderColor: "#017C57",
                                          },
                                        }),
                                        input: (base) => ({
                                          ...base,
                                          caretColor: "#017C57",
                                          color: "#191919",
                                        }),
                                        valueContainer: (base) => ({
                                          ...base,
                                          padding: "0 6px",
                                        }),
                                        indicatorsContainer: (base) => ({
                                          ...base,
                                          height: "32px",
                                        }),
                                        menu: (base) => ({
                                          ...base,
                                          width: "100%",
                                          fontSize: "12px",
                                          zIndex: 9999,
                                          marginTop: 0,
                                          marginBottom: 0,
                                          border: "1px solid #017C57",
                                          borderTop: shouldOpenUp
                                            ? "1px solid #017C57"
                                            : "0",
                                          borderBottom: shouldOpenUp
                                            ? "0"
                                            : "1px solid #017C57",
                                          borderRadius: shouldOpenUp
                                            ? "22px 22px 0 0"
                                            : "0 0 22px 22px",
                                          boxShadow: "none",
                                          overflow: "hidden",
                                        }),
                                        option: (base, state) => ({
                                          ...base,
                                          textAlign: "center",
                                          padding: "6px",
                                          whiteSpace: "normal",
                                          wordBreak: "break-word",
                                          backgroundColor: state.isFocused
                                            ? "#E8F5F0"
                                            : "#fff",
                                          color: "#191919",
                                          cursor: "pointer",
                                          ":active": {
                                            backgroundColor: state.isFocused
                                              ? "#E8F5F0"
                                              : "#fff",
                                          },
                                          ":hover": {
                                            backgroundColor: state.isFocused
                                              ? "#E8F5F0"
                                              : "#fff",
                                          },
                                        }),
                                        menuList: (base) => ({
                                          ...base,
                                          maxHeight: "150px",
                                          overflowY: "auto",
                                          overflowX: "hidden",
                                          scrollbarWidth: "none",
                                          msOverflowStyle: "none",
                                          "::-webkit-scrollbar": {
                                            width: "0px",
                                            height: "0px",
                                          },
                                        }),
                                        placeholder: (base) => ({
                                          ...base,
                                          textAlign: "center",
                                          width: "100%",
                                          color: "#7A7A7A",
                                        }),
                                        noOptionsMessage: (base) => ({
                                          ...base,
                                          textAlign: "center",
                                        }),
                                      }}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className={hideData ? "blur-[10px]" : ""}>
                      {renderMessage(q.dutMessage || "", q.id + "dut")}
                    </div>
                    <div
                      className={
                        hideData
                          ? "whitespace-pre-line blur-[10px]"
                          : "whitespace-pre-line"
                      }
                    >
                      {formatDateTime(q.tmsStageUpdateTime)}{" "}
                    </div>
                    <div className={hideData ? "blur-[10px]" : ""}>
                      {q?.case?.currentSubMasterStage?.commandCenterStage
                        ?.toLowerCase()
                        ?.includes("claim") ? (
                        new Date(q?.resolveTatTime)?.toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          },
                        ) || "--"
                      ) : q?.resolveTatTime ? (
                        <CountdownTimer targetDate={q?.resolveTatTime} />
                      ) : (
                        "--"
                      )}
                    </div>
                    <div className={hideData ? "blur-[10px]" : ""}>
                      {getNameByUserId(usersMap, q.assignedTo) || "---"}
                    </div>
                    <div
                      className={
                        hideData
                          ? "flex min-w-[120px] shrink-0 justify-end gap-2 blur-[10px] pointer-events-none"
                          : "flex min-w-[120px] shrink-0 justify-end gap-2"
                      }
                    >
                      <button
                        type="button"
                        className="flex h-12 shrink-0 items-center gap-2 rounded-full border-1 border-solid border-[#017C57] bg-transparent px-5 text-[#000000] transition hover:bg-[#017C57] hover:text-white"
                        onClick={() => handleProceedClick(q)}
                      >
                        OPEN
                        <ArrowForwardIcon className="h-5 w-5 shrink-0" />
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="min-h-[50vh] flex justify-center items-center font-semibold mt-8">
                No active Records found
              </div>
            )}
          </div>
        </div>
      )}
      <div className="flex justify-center mt-4">
        <CustomPagination
          page={page}
          setPage={setPage}
          totalPages={totalPages}
        />
      </div>
      {selectedQuery && (
        <div
          className="fixed top-0 right-0 h-screen w-screen flex justify-center items-center bg-[#017C5780] z-[1203]"
          onClick={handleCloseQueryModal}
        >
          <div
            className="relative w-[95%] h-[95%] rounded-[32px] bg-white p-6 overflow-hidden flex flex-col gap-6"
            onClick={(e) => e.stopPropagation()}
          >
            <TmsQueries
              caseId={caseId}
              setEditStatus={setSelectedQuery}
              fetchQueries={fetchQueriesAfterForward}
              fetchCaseQueryCounts={fetchCaseQueryCounts}
              onCloseModal={handleCloseQueryModal}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default DutPage;
